import axios, { AxiosError } from 'axios';
import { ENV } from '@/config/env';
import {
  PerplexityRequest,
  PerplexityResponse,
  AIInsight,
  PerplexityServiceResponse,
  SearchRecencyFilter,
  PerplexityModel,
  NewsArticle,
  StockEvent,
  IntelligentOutlook,
  StructuredMarketNews,
  StructuredStockEvents,
  StructuredIntelligentOutlook,
  MARKET_NEWS_SCHEMA,
  STOCK_EVENTS_SCHEMA,
  INTELLIGENT_OUTLOOK_SCHEMA,
} from '../../types/perplexity.types';
import { DEFAULT_MARKET_NEWS_SOURCES, STOCK_NEWS_SOURCES } from '@/constants/newsSources';

/**
 * Perplexity API Service
 * Handles all interactions with Perplexity Sonar API for AI-powered market insights
 */
class PerplexityService {
  private readonly apiUrl: string;
  private readonly apiKey: string;
  private readonly timeout: number = 30000; // 30 seconds

  constructor() {
    this.apiUrl = ENV.PERPLEXITY_API_URL;
    this.apiKey = ENV.PERPLEXITY_API_KEY;
  }

  /**
   * Validate API key exists
   */
  private validateApiKey(): void {
    if (!this.apiKey) {
      throw new Error(
        'Perplexity API key is missing. Please set EXPO_PUBLIC_PERPLEXITY_API_KEY in your environment.'
      );
    }
  }

  /**
   * Make a request to Perplexity API
   */
  private async makeRequest(
    payload: PerplexityRequest
  ): Promise<PerplexityServiceResponse<AIInsight>> {
    try {
      this.validateApiKey();

      const response = await axios.post<PerplexityResponse>(
        `${this.apiUrl}/chat/completions`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
          timeout: this.timeout,
        }
      );

      // Extract the AI response
      const content = response.data.choices[0]?.message?.content || '';
      const citations = response.data.citations || [];

      const insight: AIInsight = {
        content,
        citations,
        model: response.data.model,
        timestamp: response.data.created * 1000, // Convert to milliseconds
      };

      return {
        data: insight,
        error: null,
      };
    } catch (error) {
      console.error('Perplexity API error:', error);

      let errorMessage = 'Failed to get AI insights';

      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.data) {
          const data = axiosError.response.data as { error?: { message?: string } };
          errorMessage = data.error?.message || errorMessage;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: new Error(errorMessage),
      };
    }
  }

  /**
   * Search for market insights using recent data
   * Primary method for stock and market research
   *
   * @param query - User's market research question
   * @returns AI insight with citations
   */
  async searchMarketInsight(query: string): Promise<PerplexityServiceResponse<AIInsight>> {
    const payload: PerplexityRequest = {
      model: 'sonar' as PerplexityModel,
      messages: [
        {
          role: 'system',
          content:
            'You are a financial market analyst. Provide accurate, factual information about stocks, markets, and companies. Include relevant data points and always cite your sources. Be concise but comprehensive.',
        },
        {
          role: 'user',
          content: query,
        },
      ],
      temperature: 0.2, // Lower temperature for more factual responses
      max_tokens: 1000,
      search_recency_filter: 'day', // Focus on recent market data
      return_related_questions: true, // Get related questions if available
    };

    return this.makeRequest(payload);
  }

  /**
   * Ask a general question with optional recency filter
   *
   * @param query - User's question
   * @param recencyFilter - How recent the data should be
   * @returns AI insight with citations
   */
  async askQuestion(
    query: string,
    recencyFilter?: SearchRecencyFilter
  ): Promise<PerplexityServiceResponse<AIInsight>> {
    const payload: PerplexityRequest = {
      model: 'sonar' as PerplexityModel,
      messages: [
        {
          role: 'user',
          content: query,
        },
      ],
      temperature: 0.2,
      max_tokens: 1000,
      search_recency_filter: recencyFilter || 'week',
      return_related_questions: true,
    };

    return this.makeRequest(payload);
  }

  /**
   * Chat with explicit message array (system + last-k turns)
   * Use this to provide rolling summaries and recent context efficiently
   */
  async chat(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    recencyFilter: SearchRecencyFilter = 'week'
  ): Promise<PerplexityServiceResponse<AIInsight>> {
    const payload: PerplexityRequest = {
      model: 'sonar' as PerplexityModel,
      messages,
      temperature: 0.2,
      max_tokens: 1000,
      search_recency_filter: recencyFilter,
      return_related_questions: true,
    };

    return this.makeRequest(payload);
  }

  /**
   * Perform deep research using Sonar Pro model
   * For complex, multi-step analysis
   *
   * @param query - Complex research question
   * @returns AI insight with citations
   */
  async deepResearch(query: string): Promise<PerplexityServiceResponse<AIInsight>> {
    const payload: PerplexityRequest = {
      model: 'sonar-pro' as PerplexityModel,
      messages: [
        {
          role: 'system',
          content:
            'You are an expert financial analyst with deep knowledge of markets, economics, and company fundamentals. Provide detailed, nuanced analysis with multiple perspectives. Always cite your sources and include relevant data.',
        },
        {
          role: 'user',
          content: query,
        },
      ],
      temperature: 0.3, // Slightly higher for nuanced analysis
      max_tokens: 2000, // More tokens for detailed responses
      search_recency_filter: 'week',
      return_related_questions: true,
    };

    return this.makeRequest(payload);
  }

  /**
   * Get market news from trusted financial sources
   * For home screen news snippets
   *
   * @param recencyFilter - How recent the news should be
   * @returns AI insight with news summary from trusted sources
   */
  async getMarketNews(
    recencyFilter: SearchRecencyFilter = 'day'
  ): Promise<PerplexityServiceResponse<AIInsight>> {
    const payload: PerplexityRequest = {
      model: 'sonar' as PerplexityModel,
      messages: [
        {
          role: 'system',
          content:
            'You are a financial news summarizer. Provide a concise summary of the latest major stock market news and events. Focus on market-moving events, major company news, economic data, and significant market trends. Structure your response as a brief overview followed by 3-5 key headlines with sources.',
        },
        {
          role: 'user',
          content: 'What are the latest major stock market news and events today?',
        },
      ],
      temperature: 0.2,
      max_tokens: 800,
      search_recency_filter: recencyFilter,
      search_domain_filter: DEFAULT_MARKET_NEWS_SOURCES,
      return_related_questions: false,
    };

    return this.makeRequest(payload);
  }

  /**
   * Get stock-specific events and news
   * For stock chat events timeline
   *
   * @param ticker - Stock ticker symbol
   * @param companyName - Company name
   * @param recencyFilter - How far back to look for events
   * @returns AI insight with stock events timeline
   */
  async getStockEvents(
    ticker: string,
    companyName: string,
    recencyFilter: SearchRecencyFilter = 'week'
  ): Promise<PerplexityServiceResponse<AIInsight>> {
    const payload: PerplexityRequest = {
      model: 'sonar' as PerplexityModel,
      messages: [
        {
          role: 'system',
          content:
            'You are a stock news analyst. Provide a chronological summary of recent news and events for a specific stock. Focus on significant events like earnings reports, product launches, executive changes, analyst upgrades/downgrades, and major company announcements. Present events in reverse chronological order (most recent first) with dates, brief descriptions, and indicate if the news is positive, negative, or neutral.',
        },
        {
          role: 'user',
          content: `What are the latest news and events for ${ticker} (${companyName}) stock?`,
        },
      ],
      temperature: 0.2,
      max_tokens: 1200,
      search_recency_filter: recencyFilter,
      search_domain_filter: STOCK_NEWS_SOURCES,
      return_related_questions: false,
    };

    return this.makeRequest(payload);
  }

  /**
   * Get structured market news from trusted financial sources
   * Returns structured JSON data for beautiful card display
   *
   * @param recencyFilter - How recent the news should be
   * @returns Structured news articles array
   */
  async getMarketNewsStructured(
    recencyFilter: SearchRecencyFilter = 'day'
  ): Promise<PerplexityServiceResponse<NewsArticle[]>> {
    try {
      this.validateApiKey();

      const payload: PerplexityRequest = {
        model: 'sonar' as PerplexityModel,
        messages: [
          {
            role: 'system',
            content:
              'You are a financial news summarizer. Analyze the latest major stock market news and return structured data. For each news item, provide: headline (concise, <80 chars), summary (brief 1-2 sentences), source (website name like "Bloomberg" or "Reuters"), url (full article URL), timestamp (ISO 8601 format), and impact (positive/negative/neutral based on market sentiment). Return 10-12 most important news items for comprehensive market coverage.',
          },
          {
            role: 'user',
            content: 'What are the latest major stock market news and events today?',
          },
        ],
        temperature: 0.2,
        max_tokens: 3000,
        search_recency_filter: recencyFilter,
        search_domain_filter: DEFAULT_MARKET_NEWS_SOURCES,
        return_related_questions: false,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'market_news',
            schema: MARKET_NEWS_SCHEMA,
            strict: true,
          },
        },
      };

      const response = await axios.post<PerplexityResponse>(
        `${this.apiUrl}/chat/completions`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
          timeout: this.timeout,
        }
      );

      // Parse structured JSON response
      const content = response.data.choices[0]?.message?.content || '{}';
      const structuredData: StructuredMarketNews = JSON.parse(content);

      return {
        data: structuredData.market_news || [],
        error: null,
      };
    } catch (error) {
      console.error('Perplexity structured news error:', error);

      let errorMessage = 'Failed to get market news';
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.data) {
          const data = axiosError.response.data as { error?: { message?: string } };
          errorMessage = data.error?.message || errorMessage;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: new Error(errorMessage),
      };
    }
  }

  /**
   * Get structured stock events for timeline display
   * Returns structured JSON data for beautiful timeline
   *
   * @param ticker - Stock ticker symbol
   * @param companyName - Company name
   * @param recencyFilter - How far back to look for events
   * @returns Structured stock events array
   */
  async getStockEventsStructured(
    ticker: string,
    companyName: string,
    recencyFilter: SearchRecencyFilter = 'week'
  ): Promise<PerplexityServiceResponse<StockEvent[]>> {
    try {
      this.validateApiKey();

      const payload: PerplexityRequest = {
        model: 'sonar' as PerplexityModel,
        messages: [
          {
            role: 'system',
            content:
              'You are a stock news analyst. Analyze recent news and events for a specific stock and return structured data. For each event, provide: date (format: "Jan 24, 2025"), headline (concise, <80 chars), description (brief 1-2 sentences), source (website name), url (full article URL), event_type (earnings/news/analyst/executive/product/other), and sentiment (positive/negative/neutral). Return events in reverse chronological order (most recent first). Include 4-6 most significant events.',
          },
          {
            role: 'user',
            content: `What are the latest news and events for ${ticker} (${companyName}) stock?`,
          },
        ],
        temperature: 0.2,
        max_tokens: 1800,
        search_recency_filter: recencyFilter,
        search_domain_filter: STOCK_NEWS_SOURCES,
        return_related_questions: false,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'stock_events',
            schema: STOCK_EVENTS_SCHEMA,
            strict: true,
          },
        },
      };

      const response = await axios.post<PerplexityResponse>(
        `${this.apiUrl}/chat/completions`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
          timeout: this.timeout,
        }
      );

      // Parse structured JSON response
      const content = response.data.choices[0]?.message?.content || '{}';
      const structuredData: StructuredStockEvents = JSON.parse(content);

      return {
        data: structuredData.stock_events || [],
        error: null,
      };
    } catch (error) {
      console.error('Perplexity structured events error:', error);

      let errorMessage = 'Failed to get stock events';
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.data) {
          const data = axiosError.response.data as { error?: { message?: string } };
          errorMessage = data.error?.message || errorMessage;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: new Error(errorMessage),
      };
    }
  }

  /**
   * Get intelligent stock outlook (comprehensive AI-powered analysis)
   * Returns structured analysis combining market status, technical/fundamental outlook, and risks/catalysts
   * Designed for caching - fetched twice daily (12-hour cache)
   *
   * @param ticker - Stock ticker symbol
   * @param companyName - Company name
   * @returns Structured intelligent outlook
   */
  async getStockIntelligentOutlook(
    ticker: string,
    companyName: string
  ): Promise<PerplexityServiceResponse<IntelligentOutlook>> {
    try {
      this.validateApiKey();

      const payload: PerplexityRequest = {
        model: 'sonar' as PerplexityModel,
        messages: [
          {
            role: 'system',
            content: `You are a professional stock analyst providing comprehensive market outlooks. Analyze ${ticker} (${companyName}) and return structured data with the following sections:

1. MARKET STATUS: Provide summary (2-3 sentences about current market situation) and priceAction (recent price movement, key levels, today's performance).

2. RECENT DEVELOPMENTS: Array of 3-4 bullet points covering last 7 days - earnings, news, analyst ratings, sector trends, institutional activity.

3. TECHNICAL OUTLOOK: Analyze trend (bullish/bearish/neutral), keyLevels (support and resistance prices, use null if unclear), and summary (2-3 sentences on momentum, indicators, chart patterns).

4. FUNDAMENTAL PERSPECTIVE: Provide valuation (P/E, growth metrics), competitive (market position vs peers), and outlook (forward-looking perspective).

5. RISKS AND CATALYSTS: Provide catalysts array (3 upside opportunities) and risks array (3 downside concerns).

Be concise, data-driven, and actionable. Focus on facts and recent developments. Use professional analyst tone.`,
          },
          {
            role: 'user',
            content: `Provide a comprehensive intelligent outlook for ${ticker} (${companyName}).`,
          },
        ],
        temperature: 0.2,
        max_tokens: 2500,
        search_recency_filter: 'week',
        search_domain_filter: STOCK_NEWS_SOURCES,
        return_related_questions: false,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'intelligent_outlook',
            schema: INTELLIGENT_OUTLOOK_SCHEMA,
            strict: true,
          },
        },
      };

      const response = await axios.post<PerplexityResponse>(
        `${this.apiUrl}/chat/completions`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
          timeout: this.timeout,
        }
      );

      // Parse structured JSON response
      const content = response.data.choices[0]?.message?.content || '{}';
      const structuredData: StructuredIntelligentOutlook = JSON.parse(content);

      // Add timestamp
      const outlookWithTimestamp: IntelligentOutlook = {
        ...structuredData.intelligent_outlook,
        lastUpdated: new Date().toISOString(),
      };

      return {
        data: outlookWithTimestamp,
        error: null,
      };
    } catch (error) {
      console.error('Perplexity intelligent outlook error:', error);

      let errorMessage = 'Failed to get intelligent outlook';
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.data) {
          const data = axiosError.response.data as { error?: { message?: string } };
          errorMessage = data.error?.message || errorMessage;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: new Error(errorMessage),
      };
    }
  }
}

// Export a singleton instance
export const perplexityService = new PerplexityService();
